import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataFilters } from "components/Measure-data/Data-filters/DataFilters";
import { EditMany } from "components/Measure-data/Edit-many/EditMany";
import { defaultMeasureDataTableFilters } from "constants/measure-data";
import { useFormatDateHook, useModals } from "hooks";
import { throttle } from "lodash";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { IApiServiceError, useDateFormatSelect, useDealActions } from "store";
import { useDealDefaultCurrencySelect } from "store/selectors/deal-selectors/deal-selectors";
import { CurrencyType, MeasureDataTableFilters, MeasureDataTableRow, WideEditState, WideRow } from "store/types";

import { buildWideData, filterSplitDetailsByDataIds, filterSplitDetailsByInvestor, mergeSplitDetails } from "./helpers";
import { SetColumnsButton } from "./SetColumnsButton";

const RESERVED_HEIGHT = 150;

// ─── Measure cell with hover pencil ──────────────────────────────────────────

interface MeasureCellProps {
  row: WideRow;
  measure: string;
  onEdit: (row: WideRow, measure: string) => void;
}

const MeasureCell: React.FC<MeasureCellProps> = ({ row, measure, onEdit }) => {
  const [hovered, setHovered] = useState(false);
  const cell = row.cells[measure];
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span>{cell?.displayValue ?? "—"}</span>
      {cell && hovered && (
        <IconButton sx={{ p: 0.25 }} onClick={() => onEdit(row, measure)}>
          <CreateOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Stack>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface DealWideProps {
  dealId?: number;
  setIsEmpty?: (value: boolean) => void;
}

export const DealWide: React.FC<DealWideProps> = ({ dealId, setIsEmpty }) => {
  const { getMeasureDataTableAction, getSplitDetailsAction, deleteMeasureDataAction } = useDealActions();
  const { deleteModal, errorModal } = useModals();
  const queryClient = useQueryClient();
  const currency = (useDealDefaultCurrencySelect() || "USD") as CurrencyType;
  const formatDate = useFormatDateHook();
  const dateFormat = useDateFormatSelect();

  const [filters, setFilters] = useState<MeasureDataTableFilters>(defaultMeasureDataTableFilters);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [editState, setWideEditState] = useState<WideEditState | null>(null);

  // ── Paginated fetch ───────────────────────────────────────────────────────
  const [allRows, setAllRows] = useState<MeasureDataTableRow[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const updateFilters = useCallback((data: Partial<MeasureDataTableFilters> | null) => {
    setAllRows([]);
    setHasMore(true);
    setPage(1);
    if (data === null) {
      setFilters(defaultMeasureDataTableFilters);
    } else {
      setFilters((prev) => ({ ...prev, ...data }));
    }
  }, []);

  // dealId is optional: when absent (global view) all deals are fetched
  const {
    data: tableData,
    isLoading,
    isFetching
  } = useQuery(
    ["deal-wide-data", dealId, page, filters],
    () =>
      getMeasureDataTableAction({
        page,
        per_page: 100,
        showTopInvestors: true,
        is_general: !dealId,
        filters: {
          ...filters,
          ...(dealId ? { deals: [{ id: dealId, title: "" }] } : {})
        },
        sort: { order: "asc", orderBy: "date" }
      }),
    {
      enabled: true,
      keepPreviousData: true,
      onSuccess: (d) => {
        setAllRows((prev) => (page === 1 ? d.rows : [...prev, ...d.rows]));
        setHasMore(Number(d.current_page) < d.last_page);
        if (page === 1) setIsEmpty?.(d.rows.length === 0);
      }
    }
  );

  const refetchAll = useCallback(() => {
    setAllRows([]);
    setHasMore(true);
    setPage(1);
    queryClient.invalidateQueries(["deal-wide-data", dealId]);
  }, [dealId, queryClient]);

  // ── Infinite scroll via MRT onScroll ──────────────────────────────────────
  // Use refs to avoid stale closures in the scroll handler
  const scrollStateRef = useRef({ isFetching, hasMore });
  useEffect(() => {
    scrollStateRef.current = { isFetching, hasMore };
  });

  const throttledCheck = useMemo(
    () =>
      throttle((scrollHeight: number, clientHeight: number, scrollTop: number) => {
        const { isFetching: fetching, hasMore: more } = scrollStateRef.current;
        if (fetching || !more) return;
        if (scrollHeight - clientHeight - scrollTop < RESERVED_HEIGHT) {
          setPage((prev) => prev + 1);
        }
      }, 200),
    []
  );

  const handleTableScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollHeight, clientHeight, scrollTop } = e.currentTarget;
      throttledCheck(scrollHeight, clientHeight, scrollTop);
    },
    [throttledCheck]
  );

  // ── Build wide rows ─────────────────────────────────────────────────────────
  const { wideRows, measureColumns } = useMemo(() => {
    if (!allRows.length) return { wideRows: [], measureColumns: [] };
    return buildWideData(allRows);
  }, [allRows]);

  // ── Edit handlers ───────────────────────────────────────────────────────────
  const openEdit = useCallback(
    async (row: WideRow, measure?: string) => {
      if (!dealId || !row.batchIds.length) return;
      try {
        const allDetails = await Promise.all(
          row.batchIds.map((batchId) => getSplitDetailsAction({ deal_id: dealId, batch_id: batchId }))
        );
        let result = mergeSplitDetails(allDetails);

        if (measure) {
          const cellDataIds = row.isAggregated
            ? new Set(row.children.map((c) => c.cells[measure]?.dataId).filter((id): id is number => !!id && id > 0))
            : (() => {
                const id = row.cells[measure]?.dataId;
                return id && id > 0 ? new Set([id]) : new Set<number>();
              })();
          if (cellDataIds.size > 0) result = filterSplitDetailsByDataIds(result, cellDataIds);
        }

        if (!row.isAggregated && row.investor) {
          result = filterSplitDetailsByInvestor(result, row.investor);
        }

        setWideEditState({ splitDetails: result, batchId: row.batchIds[0], investorName: row.investor });
      } catch (err) {
        errorModal({ msg: (err as IApiServiceError)?.message });
      }
    },
    [dealId, getSplitDetailsAction, errorModal]
  );

  const handleEditRow = useCallback((row: WideRow) => openEdit(row), [openEdit]);
  const handleEditCell = useCallback((row: WideRow, measure: string) => openEdit(row, measure), [openEdit]);

  const handleEditClose = useCallback(() => {
    setWideEditState(null);
    refetchAll();
  }, [refetchAll]);

  // ── Delete handler ──────────────────────────────────────────────────────────
  const handleDeleteRow = useCallback(
    (row: WideRow) => {
      const ids = row.allDataIds;
      if (!ids.length) return;
      const subject = row.isAggregated ? `batch entry for ${row.date}` : `${row.investor ?? "entry"} on ${row.date}`;
      deleteModal({
        element: "entry",
        message: `This will delete ${ids.length} measure entr${ids.length === 1 ? "y" : "ies"} for ${subject}. This action cannot be undone.`,
        action: async () => {
          await Promise.all(ids.map((id) => deleteMeasureDataAction(id)));
          refetchAll();
        }
      });
    },
    [deleteModal, deleteMeasureDataAction, dealId, queryClient]
  );

  // ── Stable ref for callbacks used inside columns ────────────────────────────
  // useFormatDateHook() returns a new function reference every render, which
  // would cause useMemo(columns) to recompute → MRT internal state reset →
  // infinite re-render loop. Capture everything in a ref instead.
  const cbRef = useRef({ handleEditRow, handleEditCell, handleDeleteRow, formatDate, dateFormat });
  useEffect(() => {
    cbRef.current = { handleEditRow, handleEditCell, handleDeleteRow, formatDate, dateFormat };
  });

  // ── MRT column definitions ──────────────────────────────────────────────────
  const columns = useMemo<MRT_ColumnDef<WideRow>[]>(() => {
    const dealCol: MRT_ColumnDef<WideRow>[] = !dealId
      ? [{ id: "deal", header: "Deal", accessorFn: (row) => row.deal ?? "—", size: 180, enableColumnOrdering: false }]
      : [];

    const fixed: MRT_ColumnDef<WideRow>[] = [
      {
        id: "date",
        header: "Date",
        accessorFn: (row) =>
          row.date ? cbRef.current.formatDate({ date: row.date, format: cbRef.current.dateFormat }) : "—",
        enableColumnOrdering: false,
        size: 110
      },
      {
        id: "round",
        accessorKey: "round",
        header: "Round",
        size: 120
      },
      {
        id: "investor",
        header: "Investor",
        accessorFn: (row) => (row.isAggregated ? "All investors" : (row.investor ?? "—")),
        Cell: ({ row }) =>
          row.original.isAggregated ? (
            <Typography component="span" sx={{ fontSize: "inherit", fontStyle: "italic", color: "#71A0B2" }}>
              All investors
            </Typography>
          ) : (
            <span>{row.original.investor ?? "—"}</span>
          ),
        size: 160
      },
      {
        id: "bankAccount",
        header: "Bank Account",
        accessorFn: (row) => row.bankAccount ?? "—",
        size: 140
      }
    ];

    const measureCols: MRT_ColumnDef<WideRow>[] = measureColumns.map((measure) => ({
      id: measure,
      header: measure,
      accessorFn: (row) => row.cells[measure]?.displayValue ?? "—",
      Cell: ({ row }) => (
        <MeasureCell row={row.original} measure={measure} onEdit={(r, m) => cbRef.current.handleEditCell(r, m)} />
      ),
      size: 130
    }));

    const actionCol: MRT_ColumnDef<WideRow> = {
      id: "_actions",
      header: "",
      enableColumnFilter: false,
      enableColumnOrdering: false,
      enableResizing: false,
      enablePinning: true,
      size: 72,
      minSize: 72,
      maxSize: 72,
      muiTableHeadCellProps: {
        sx: {
          padding: "0 !important",
          backgroundColor: "#edf1f9 !important",
          borderBottom: "1px solid #DBE8F5 !important",
          "&::before": { display: "none" }
        }
      },
      muiTableBodyCellProps: {
        className: "pinned-actions-cell",
        sx: {
          padding: "0 !important",
          position: "sticky",
          right: 0,
          zIndex: 1,
          backgroundColor: "white",
          borderBottom: "1px solid #DBE8F5 !important",
          "&::before": { display: "none" }
        }
      },
      Cell: ({ row }) => (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          className="row-actions"
          onClick={(e) => e.stopPropagation()}
          sx={{
            height: "100%",
            px: "4px",
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.15s"
          }}
        >
          <IconButton onClick={() => cbRef.current.handleEditRow(row.original)} sx={{ padding: "4px" }}>
            <CreateOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton onClick={() => cbRef.current.handleDeleteRow(row.original)} sx={{ padding: "4px" }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      )
    };

    return [...dealCol, ...fixed, ...measureCols, actionCol];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureColumns, dealId]);

  // ── MRT table instance ──────────────────────────────────────────────────────
  const table = useMaterialReactTable<WideRow>({
    columns,
    data: wideRows,
    getRowId: (row) => row.key,
    getSubRows: (row) => (row.children.length > 0 ? row.children : undefined),

    // Enabled features
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnOrdering: true,
    enableExpanding: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",

    // Disabled features
    enableSorting: false,
    enablePagination: false,
    enableRowSelection: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,

    state: { columnVisibility, columnPinning: { right: ["_actions"] } },
    enableColumnPinning: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableHiding: true,

    layoutMode: "grid",

    icons: {
      DragHandleIcon: () => <DragIndicatorIcon sx={{ fontSize: 16 }} />
    },

    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid #DBE8F5",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        overflow: "hidden",
        "& th[data-pinned='true']::after, & td[data-pinned='true']::after": { boxShadow: "none !important" },
        "& th[data-pinned='true'], & td[data-pinned='true']": { boxShadow: "none !important" }
      }
    },
    muiTableContainerProps: {
      onScroll: handleTableScroll,
      sx: { flexGrow: 1, maxHeight: "100%" }
    },
    muiTableHeadCellProps: ({ column }) => ({
      title: (column.columnDef.header as string) || undefined,
      sx: {
        bgcolor: "#edf1f9 !important",
        fontSize: "13px",
        fontWeight: 500,
        color: "#071F35",
        borderBottom: "1px solid #DBE8F5",
        py: "4px",
        px: "16px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        "& .Mui-TableHeadCell-Content": { height: "100%" },
        "& .Mui-TableHeadCell-Content-Labels": { flex: 1, minWidth: 0, overflow: "hidden" },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        },
        "& .MuiDivider-root:not(.Mui-TableHeadCell-ResizeHandle-Divider)": { display: "none" },
        "& .Mui-TableHeadCell-ResizeHandle-Divider": {
          borderColor: "#bcbfcf !important",
          opacity: 0,
          borderWidth: "1px !important",
          transition: "opacity 0.15s"
        },
        "&:hover .Mui-TableHeadCell-ResizeHandle-Divider": {
          opacity: 1
        },
        "&::before": { display: "none" }
      }
    }),
    muiTableHeadRowProps: {
      sx: {
        boxShadow: "none",
        height: "40px",
        minHeight: "40px",
        "& th": { height: "40px", minHeight: "40px" }
      }
    },
    muiTableBodyCellProps: ({ row, column }) => ({
      sx: {
        fontSize: "13px",
        color: "#071F35",
        borderBottom: "1px solid #dbe8f5",
        height: 40,
        py: "4px",
        px: "16px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        "tr:hover &": { bgcolor: "#f7f8fa !important" },
        ...(row.depth > 0 && column.id === "date" && { paddingLeft: `${16 + row.depth * 24}px` })
      }
    }),
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: "40px",
        minHeight: "40px",
        bgcolor: row.depth > 0 ? "rgba(246,248,252,0.5)" : "white",
        "&:hover .row-actions": { opacity: 1, pointerEvents: "auto" as const },
        "&:hover td": { bgcolor: "#f7f8fa !important" },
        "&:hover .pinned-actions-cell": { bgcolor: "#f7f8fa !important" },
        "&:last-child td": { borderBottom: "none" }
      }
    })
  });

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!allRows.length) {
    if (tableData && tableData.rows.length === 0 && !isFetching) {
      return (
        <Typography variant="body2" sx={{ color: "#71A0B2", p: 2 }}>
          No monitoring data available.
        </Typography>
      );
    }
    return (
      <Stack gap={1} sx={{ p: 2 }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: "10px", flexShrink: 0 }}>
        <DataFilters filters={filters} setFilters={updateFilters} disabled={isLoading} hideShowDeleted={true} />
        <SetColumnsButton
          measureColumns={measureColumns}
          columnVisibility={columnVisibility}
          onToggle={(col) => setColumnVisibility((prev) => ({ ...prev, [col]: prev[col] === false }))}
          onToggleAll={(show) =>
            setColumnVisibility(
              measureColumns.reduce<Record<string, boolean>>((acc, col) => {
                acc[col] = show;
                return acc;
              }, {})
            )
          }
        />
      </Stack>

      {/* MRT Table */}
      <MaterialReactTable table={table} />

      {/* Edit Drawer */}
      {editState && (
        <EditMany
          splitDetails={editState.splitDetails}
          deal_id={dealId}
          batch_id={editState.batchId}
          defaultCurrency={currency}
          handleClose={handleEditClose}
        />
      )}
    </Stack>
  );
};
